import React from 'react';
import { Schedule, CheckCircle, AccountBalance, Cancel, ContentCopy, Send, Visibility, Link } from '@mui/icons-material';

export const PaymentLinkRow = ({ link, onViewDetails, onCopyLink, onResendLink, onCancelLink }) => {
    const getStatusConfig = (status) => {
        const configs = {
            'pending': {
                color: 'bg-amber-100 text-amber-800 border-amber-200',
                icon: Schedule,
                dotColor: 'bg-amber-400'
            },
            'paid': {
                color: 'bg-blue-100 text-blue-800 border-blue-200',
                icon: CheckCircle,
                dotColor: 'bg-blue-400'
            },
            'collected': {
                color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
                icon: AccountBalance,
                dotColor: 'bg-emerald-400'
            },
            'cashed': {
                color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
                icon: AccountBalance,
                dotColor: 'bg-emerald-400'
            },
            'expired': {
                color: 'bg-rose-100 text-rose-800 border-rose-200',
                icon: Cancel,
                dotColor: 'bg-rose-400'
            }
        };
        return configs[status] || configs.pending;
    };

    const formatCurrency = (amount, currency) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusText = (status) => {
        return status === 'pending' ? 'En attente' :
            status === 'paid' ? 'Payé' :
                (status === 'collected' || status === 'cashed') ? 'Encaissé' : 'Expiré';
    };

    const statusConfig = getStatusConfig(link.status);
    const isExpired = new Date(link.expiresAt) < new Date();
    const daysLeft = Math.ceil((new Date(link.expiresAt) - new Date()) / (1000 * 60 * 60 * 24));

    return (
        <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-200 group">
            {/* Lien & Client */}
            <td className="py-4 px-4">
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-slate-800 to-slate-700 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                        <Link className="text-white text-sm" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 text-sm truncate max-w-[200px]">
                            {link.title}
                        </div>
                        <div className="text-xs text-slate-500 truncate max-w-[200px]">
                            {link.client || 'Client non spécifié'}
                        </div>
                    </div>
                </div>
            </td>

            {/* Montant */}
            <td className="py-4 px-4">
                <div className="font-semibold text-slate-900 text-sm">
                    {formatCurrency(link.amount, link.currency)}
                </div>
                <div className="text-xs text-slate-500 uppercase">
                    {link.currency}
                </div>
            </td>

            {/* Statistiques */}
            <td className="py-4 px-4">
                <div className="flex items-center space-x-4 text-sm">
                    <div className="text-center">
                        <div className="font-semibold text-slate-900">{link.clicks || 0}</div>
                        <div className="text-xs text-slate-500">Clics</div>
                    </div>
                    <div className="w-px h-6 bg-slate-200"></div>
                    <div className="text-center">
                        <div className="font-semibold text-slate-900">{link.payments || 0}</div>
                        <div className="text-xs text-slate-500">Paiements</div>
                    </div>
                </div>
            </td>

            {/* Dates */}
            <td className="py-4 px-4">
                <div className="flex flex-col space-y-1">
                    <div className="text-sm text-slate-900">
                        {formatDate(link.createdAt)}
                    </div>
                    <div className="text-xs text-slate-500">
                        Expire: {formatDate(link.expiresAt)}
                    </div>
                    {link.status === 'pending' && !isExpired && (
                        <div className={`text-xs font-medium ${daysLeft <= 2 ? 'text-rose-600' : 'text-amber-600'}`}>
                            {daysLeft} jour(s) restant(s)
                        </div>
                    )}
                </div>
            </td>

            {/* Statut */}
            <td className="py-4 px-4">
                <span className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                    <div className={`w-2 h-2 rounded-full ${statusConfig.dotColor}`}></div>
                    <span>{getStatusText(link.status)}</span>
                </span>
            </td>

            {/* Actions */}
            <td className="py-4 px-4">
                <div className="flex items-center space-x-1">
                    {/* Bouton Détails */}
                    <button
                        onClick={() => onViewDetails(link)}
                        className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 group/btn"
                        title="Voir détails"
                    >
                        <Visibility className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                    </button>

                    {/* Bouton Copier - seulement si ce n'est pas un paiement flash */}
                    {link.method !== 'flash' && (
                        <button
                            onClick={() => onCopyLink(link)}
                            className="p-2 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all duration-200 group/btn"
                            title="Copier le lien"
                        >
                            <ContentCopy className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                        </button>
                    )}

                    {/* Bouton Renvoyer (seulement pour pending) */}
                    {link.status === 'pending' && (
                        <button
                            onClick={() => onResendLink(link)}
                            className="p-2 text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all duration-200 group/btn"
                            title="Renvoyer le lien"
                        >
                            <Send className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                        </button>
                    )}

                    {/* Bouton Annuler (seulement pour pending) */}
                    {link.status === 'pending' && (
                        <button
                            onClick={() => onCancelLink(link)}
                            className="p-2 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all duration-200 group/btn"
                            title="Annuler le lien"
                        >
                            <Cancel className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
};

// Composant parent pour afficher le tableau complet
export const PaymentLinksTable = ({ links, onViewDetails, onCopyLink, onResendLink, onCancelLink, hidePagination = false }) => {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* En-tête du tableau */}
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">Liens de Paiement</h3>
                        <p className="text-sm text-slate-600">{links.length} lien(s) de paiement</p>
                    </div>
                </div>
            </div>

            {/* Tableau */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                        <th className="py-4 px-4 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">
                            Lien & Client
                        </th>
                        <th className="py-4 px-4 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">
                            Montant
                        </th>
                        <th className="py-4 px-4 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">
                            Statistiques
                        </th>
                        <th className="py-4 px-4 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">
                            Dates
                        </th>
                        <th className="py-4 px-4 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">
                            Statut
                        </th>
                        <th className="py-4 px-4 text-left text-sm font-semibold text-slate-700 whitespace-nowrap">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                    {links.map((link) => (
                        <PaymentLinkRow
                            key={link.id}
                            link={link}
                            onViewDetails={onViewDetails}
                            onCopyLink={onCopyLink}
                            onResendLink={onResendLink}
                            onCancelLink={onCancelLink}
                        />
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {!hidePagination && links.length > 0 && (
                <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-600">
                            Affichage de <span className="font-semibold">{links.length}</span> lien(s)
                        </p>
                        <div className="flex items-center space-x-2">
                            <button className="px-3 py-2 border border-slate-300 rounded-xl text-slate-600 hover:bg-slate-100 transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                                Précédent
                            </button>
                            <button className="px-3 py-2 bg-gradient-to-r from-amber-500 to-amber-500 text-white rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 text-sm shadow-sm">
                                1
                            </button>
                            <button className="px-3 py-2 border border-slate-300 rounded-xl text-slate-600 hover:bg-slate-100 transition-all duration-200 text-sm">
                                2
                            </button>
                            <button className="px-3 py-2 border border-slate-300 rounded-xl text-slate-600 hover:bg-slate-100 transition-all duration-200 text-sm">
                                Suivant
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Exemple d'utilisation dans un composant parent
export const PaymentLinksManager = () => {
    // Données mock pour l'exemple
    const mockLinks = [
        {
            id: 'LINK-001',
            title: 'Facture Services Consulting',
            client: 'Entreprise ABC',
            amount: 1500.00,
            currency: 'USD',
            clicks: 12,
            payments: 1,
            status: 'pending',
            createdAt: new Date('2024-02-01T10:00:00'),
            expiresAt: new Date('2024-02-08T10:00:00')
        },
        {
            id: 'LINK-002',
            title: 'Paiement Abonnement Mensuel',
            client: 'Client Premium',
            amount: 99.99,
            currency: 'USD',
            clicks: 8,
            payments: 1,
            status: 'paid',
            createdAt: new Date('2024-01-28T14:30:00'),
            expiresAt: new Date('2024-02-04T14:30:00')
        },
        {
            id: 'LINK-003',
            title: 'Achat Produits Digital',
            client: 'Particulier',
            amount: 49.50,
            currency: 'USD',
            clicks: 15,
            payments: 1,
            status: 'collected',
            createdAt: new Date('2024-01-25T09:15:00'),
            expiresAt: new Date('2024-02-01T09:15:00')
        },
        {
            id: 'LINK-004',
            title: 'Réservation Événement',
            client: 'Association XYZ',
            amount: 250.00,
            currency: 'USD',
            clicks: 5,
            payments: 0,
            status: 'expired',
            createdAt: new Date('2024-01-20T16:45:00'),
            expiresAt: new Date('2024-01-27T16:45:00')
        }
    ];

    const handleViewDetails = (link) => {
        console.log('Voir détails:', link);
        // Implémentez la logique pour afficher les détails
    };

    const handleCopyLink = (link) => {
        console.log('Copier lien:', link);
        // Implémentez la logique de copie
        navigator.clipboard.writeText(`https://payment.link/${link.id}`);
    };

    const handleResendLink = (link) => {
        console.log('Renvoyer lien:', link);
        // Implémentez la logique de renvoi
    };

    const handleCancelLink = (link) => {
        console.log('Annuler lien:', link);
        // Implémentez la logique d'annulation
    };

    return (
        <div className="p-6">
            <PaymentLinksTable
                links={mockLinks}
                onViewDetails={handleViewDetails}
                onCopyLink={handleCopyLink}
                onResendLink={handleResendLink}
                onCancelLink={handleCancelLink}
            />
        </div>
    );
};

export default PaymentLinksManager;